import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';
import { useAuth } from './useAuth';
import type { Team, TeamMember, TeamSermonShare, TeamRole, TeamPermission, UserPermission } from '../types/teams';

export function useTeams() {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [pendingInvites, setPendingInvites] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTeams = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);

      // Load teams the user owns or is an accepted member of
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('*')
        .order('created_at', { ascending: true });

      if (teamsError) throw teamsError;

      // For each team, load members
      const teamsWithMembers: Team[] = [];
      for (const team of teamsData || []) {
        const { data: membersData, error: membersError } = await supabase
          .from('team_members')
          .select('*')
          .eq('team_id', team.id)
          .order('invited_at', { ascending: true });

        if (membersError) throw membersError;

        // Enrich accepted members with profile names
        const memberIds = (membersData || [])
          .filter(m => m.user_id)
          .map(m => m.user_id as string);

        let profilesMap: Record<string, string> = {};
        if (memberIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', memberIds);
          (profiles || []).forEach(p => {
            profilesMap[p.id] = p.full_name || '';
          });
        }

        const enrichedMembers: TeamMember[] = (membersData || []).map(m => ({
          ...m,
          full_name: m.user_id ? profilesMap[m.user_id] || m.invited_email : m.invited_email,
        }));

        teamsWithMembers.push({ ...team, members: enrichedMembers });
      }

      setTeams(teamsWithMembers);

      // Load pending invites for the current user (invites sent to their email)
      const { data: userAuthData } = await supabase.auth.getUser();
      const userEmail = userAuthData.user?.email;
      if (userEmail) {
        const { data: invitesData, error: invitesError } = await supabase
          .from('team_members')
          .select('*, teams(name)')
          .eq('invited_email', userEmail)
          .eq('status', 'pending');

        if (invitesError) throw invitesError;
        setPendingInvites((invitesData || []) as any);
      }
    } catch (e) {
      console.error('Error loading teams:', e);
      setError(e instanceof Error ? e.message : 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) loadTeams();
  }, [user, loadTeams]);

  // ── Team CRUD ────────────────────────────────────────────────────────────

  const createTeam = useCallback(async (name: string): Promise<Team | null> => {
    if (!user) return null;
    try {
      const { data, error } = await supabase
        .from('teams')
        .insert({ name, created_by: user.id })
        .select()
        .single();
      if (error) throw error;
      await loadTeams();
      return data;
    } catch (e) {
      console.error('Error creating team:', e);
      throw e;
    }
  }, [user, loadTeams]);

  const renameTeam = useCallback(async (teamId: string, name: string) => {
    try {
      const { error } = await supabase
        .from('teams')
        .update({ name })
        .eq('id', teamId);
      if (error) throw error;
      await loadTeams();
    } catch (e) {
      console.error('Error renaming team:', e);
      throw e;
    }
  }, [loadTeams]);

  const deleteTeam = useCallback(async (teamId: string) => {
    try {
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);
      if (error) throw error;
      await loadTeams();
    } catch (e) {
      console.error('Error deleting team:', e);
      throw e;
    }
  }, [loadTeams]);

  // ── Member management ────────────────────────────────────────────────────

  const inviteMember = useCallback(async (
    teamId: string,
    email: string,
    role: TeamRole,
    message?: string
  ) => {
    if (!user) throw new Error('Not authenticated');
    try {
      // Check if already a member
      const { data: existing } = await supabase
        .from('team_members')
        .select('id, status')
        .eq('team_id', teamId)
        .eq('invited_email', email.toLowerCase())
        .maybeSingle();

      if (existing && existing.status !== 'declined') {
        throw new Error('This person has already been invited to this team');
      }

      // Check if the email belongs to an existing user
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email.toLowerCase())
        .maybeSingle();

      const insertData: any = {
        team_id: teamId,
        invited_email: email.toLowerCase(),
        role,
        status: 'pending',
        invited_by: user.id,
      };

      if (profiles?.id) {
        insertData.user_id = profiles.id;
      }

      // If there's a declined record, delete it first and re-invite
      if (existing?.status === 'declined') {
        await supabase.from('team_members').delete().eq('id', existing.id);
      }

      const { error } = await supabase.from('team_members').insert(insertData);
      if (error) throw error;

      // Trigger invite email via Edge Function. A send failure is non-fatal —
      // the invite row already exists — but it must be reported, not swallowed:
      // the function returns { success: true, emailSent: false } on failure, so
      // without this the UI would claim success while nothing was delivered.
      let emailSent = false;
      let emailError: string | undefined;
      try {
        const { data, error: invokeError } = await supabase.functions.invoke('invite-team-member', {
          body: { teamId, invitedEmail: email.toLowerCase(), role, message },
        });
        if (invokeError) {
          emailError = invokeError.message;
        } else {
          emailSent = data?.emailSent === true;
          emailError = data?.emailError;
        }
      } catch (emailErr) {
        emailError = emailErr instanceof Error ? emailErr.message : String(emailErr);
      }

      if (!emailSent) {
        logger.error('Invite email not sent (invite row still created):', emailError);
      }

      await loadTeams();
      return { emailSent, emailError };
    } catch (e) {
      console.error('Error inviting member:', e);
      throw e;
    }
  }, [user, loadTeams]);

  const removeMember = useCallback(async (teamMemberId: string) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', teamMemberId);
      if (error) throw error;
      await loadTeams();
    } catch (e) {
      console.error('Error removing member:', e);
      throw e;
    }
  }, [loadTeams]);

  const updateMemberRole = useCallback(async (teamMemberId: string, role: TeamRole) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({ role })
        .eq('id', teamMemberId);
      if (error) throw error;
      await loadTeams();
    } catch (e) {
      console.error('Error updating member role:', e);
      throw e;
    }
  }, [loadTeams]);

  // ── Invite acceptance ────────────────────────────────────────────────────

  const getInviteByToken = useCallback(async (token: string): Promise<TeamMember & { teams: { name: string } } | null> => {
    try {
      const { data, error } = await supabase
        .from('team_members')
        .select('*, teams(name)')
        .eq('invite_token', token)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    } catch (e) {
      console.error('Error fetching invite:', e);
      return null;
    }
  }, []);

  const acceptInvite = useCallback(async (token: string) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const { data: authUser } = await supabase.auth.getUser();
      const userEmail = authUser.user?.email;

      const invite = await getInviteByToken(token);
      if (!invite) throw new Error('Invite not found');
      if (invite.status !== 'pending') throw new Error('This invite has already been responded to');
      if (invite.invite_expires_at && new Date(invite.invite_expires_at) < new Date()) {
        throw new Error('This invite has expired');
      }
      if (invite.invited_email !== userEmail?.toLowerCase()) {
        throw new Error(`This invite was sent to ${invite.invited_email}. Please sign in with that account.`);
      }

      const { error } = await supabase
        .from('team_members')
        .update({ status: 'accepted', user_id: user.id, accepted_at: new Date().toISOString() })
        .eq('invite_token', token);
      if (error) throw error;

      // Auto-grant collaborator tier if the user has no existing subscription.
      // Non-fatal: if this fails the invite is still accepted.
      const { error: grantError } = await supabase.rpc('grant_collaborator_access');
      if (grantError) console.warn('grant_collaborator_access failed (non-fatal):', grantError);

      await loadTeams();
    } catch (e) {
      console.error('Error accepting invite:', e);
      throw e;
    }
  }, [user, getInviteByToken, loadTeams]);

  const declineInvite = useCallback(async (token: string) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({ status: 'declined' })
        .eq('invite_token', token);
      if (error) throw error;
      await loadTeams();
    } catch (e) {
      console.error('Error declining invite:', e);
      throw e;
    }
  }, [loadTeams]);

  // ── Sermon sharing ───────────────────────────────────────────────────────

  const getSermonShares = useCallback(async (sermonId: string): Promise<TeamSermonShare[]> => {
    try {
      const { data, error } = await supabase
        .from('team_sermon_shares')
        .select('*, team:teams(id, name)')
        .eq('sermon_id', sermonId);
      if (error) throw error;
      return (data || []) as any;
    } catch (e) {
      console.error('Error fetching sermon shares:', e);
      return [];
    }
  }, []);

  const shareSermonWithTeam = useCallback(async (
    sermonId: string,
    teamId: string,
    permission: TeamPermission
  ) => {
    if (!user) throw new Error('Not authenticated');
    try {
      const { error } = await supabase
        .from('team_sermon_shares')
        .upsert(
          { sermon_id: sermonId, team_id: teamId, shared_by: user.id, permission },
          { onConflict: 'sermon_id,team_id' }
        );
      if (error) throw error;
    } catch (e) {
      console.error('Error sharing sermon with team:', e);
      throw e;
    }
  }, [user]);

  const unshareSermonFromTeam = useCallback(async (sermonId: string, teamId: string) => {
    try {
      const { error } = await supabase
        .from('team_sermon_shares')
        .delete()
        .eq('sermon_id', sermonId)
        .eq('team_id', teamId);
      if (error) throw error;
    } catch (e) {
      console.error('Error unsharing sermon from team:', e);
      throw e;
    }
  }, []);

  const updateSermonSharePermission = useCallback(async (
    sermonId: string,
    teamId: string,
    permission: TeamPermission
  ) => {
    try {
      const { error } = await supabase
        .from('team_sermon_shares')
        .update({ permission })
        .eq('sermon_id', sermonId)
        .eq('team_id', teamId);
      if (error) throw error;
    } catch (e) {
      console.error('Error updating sermon share permission:', e);
      throw e;
    }
  }, []);

  // Returns the effective permission for the current user on a given sermon
  const getSermonPermission = useCallback(async (sermonId: string): Promise<UserPermission | null> => {
    if (!user) return null;
    try {
      // Check ownership
      const { data: sermon } = await supabase
        .from('sermons')
        .select('user_id')
        .eq('id', sermonId)
        .maybeSingle();

      if (!sermon) return null;
      if (sermon.user_id === user.id) return 'owner';

      // Check team shares
      const { data: shares } = await supabase
        .from('team_sermon_shares')
        .select('permission, team:teams(team_members(user_id, status, role))')
        .eq('sermon_id', sermonId);

      if (!shares || shares.length === 0) return null;

      let highestPermission: UserPermission | null = null;
      for (const share of shares) {
        const team = share.team as any;
        const members: any[] = team?.team_members || [];
        const membership = members.find(m => m.user_id === user.id && m.status === 'accepted');
        if (membership) {
          if (share.permission === 'editor') return 'editor';
          highestPermission = 'viewer';
        }
      }
      return highestPermission;
    } catch (e) {
      console.error('Error getting sermon permission:', e);
      return null;
    }
  }, [user]);

  return {
    teams,
    pendingInvites,
    loading,
    error,
    refresh: loadTeams,
    createTeam,
    renameTeam,
    deleteTeam,
    inviteMember,
    removeMember,
    updateMemberRole,
    getInviteByToken,
    acceptInvite,
    declineInvite,
    getSermonShares,
    shareSermonWithTeam,
    unshareSermonFromTeam,
    updateSermonSharePermission,
    getSermonPermission,
  };
}
